import type { ActorRefFrom, AnyActorRef } from 'xstate';
import { assign, enqueueActions, setup } from 'xstate';
import { ALERT_VIEW_STATE, DOCUMENTS_SERVICE } from '~/features/xstateSystem';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  STORABLE_EXTENSIONS,
  toTypesLabel,
} from './constants';
import { documentsService } from './documents.service.machine';
import { Events } from './events';
import type { DocumentInfoValues, UploadedDocument } from './types';
import {
  buildUploadJobs,
  metadataFor,
  summarizeUploads,
  toOutcome,
  type UploadBatch,
  type UploadJob,
} from './uploadBatch';
import { getRejectionAlert, splitUploadable } from './utils';

type DocumentsServiceRef = ActorRefFrom<typeof documentsService>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- system shape is loose across actor trees
type LooseSystem = any;

// One place to raise an alert; the alert actor may not exist (or may throw) and that must not
// break the upload flow.
const sendAlert = (
  system: LooseSystem,
  payload: { text: string; subText?: string; type: string; timeout: number; persistent?: boolean }
) => {
  try {
    system?.get(ALERT_VIEW_STATE)?.send({ type: 'ON_SHOW_ALERT', payload });
  } catch (error) {
    console.error('[VIEW] Failed to send alert:', error);
  }
};

const NOT_READY_ALERT = {
  text: 'Upload not ready',
  subText: 'Your documents workspace is still loading. Please wait a moment and try again.',
  type: 'error',
  timeout: 5000,
};

const inProgressAlert = (subText: string) => ({
  text: 'Upload in progress',
  subText,
  type: 'error',
  timeout: 5000,
});

// MCV-605/1190: Upload was silently dropped when the view machine's context.providerId
// was undefined (happens when $_DOCUMENTS_VIEW is entered before $_LOAD_PROVIDER
// completes). Resolve the target in one place so every upload path gets the same fallback
// to the documentsService snapshot.
const resolveUploadTarget = ({
  context,
  system,
}: {
  context: { providerId?: number; documentsService: DocumentsServiceRef };
  system: LooseSystem;
}) => {
  const svc = (system?.get(DOCUMENTS_SERVICE) ||
    context.documentsService) as DocumentsServiceRef | null;
  const providerId = context.providerId ?? svc?.getSnapshot().context.providerId ?? null;
  return { svc, providerId };
};

// One POST to the single-flight documents service: a lone file is a document of its own, 2+ files
// are combined into one packet document.
const sendUpload = (
  svc: DocumentsServiceRef,
  self: AnyActorRef,
  batch: Pick<UploadBatch, 'providerId' | 'values' | 'total'>,
  job: UploadJob
) => {
  const values = metadataFor(job, batch.values, batch.total);
  if (job.files.length > 1) {
    svc.send({
      type: 'UPLOAD_COMBINED_DOCUMENT',
      providerId: batch.providerId,
      files: job.files,
      viewRef: self,
      values,
    });
    return;
  }
  svc.send({
    type: 'UPLOAD_DOCUMENT',
    providerId: batch.providerId,
    file: job.files[0],
    viewRef: self,
    values,
  });
};

// The "categorise it now" form for a single dropped file, filled from the document it created.
const formForDocument = (document: UploadedDocument) => ({
  id: document.id,
  docName: document.docName,
  uploadedFileName: document.uploadedFileName,
  docDesc: (document.docDesc ?? '').trim(),
  docType: DOCUMENT_TYPES.find(({ value }) => value === document.docType) || {
    label: '',
    value: '',
  },
  docCategory: DOCUMENT_CATEGORIES.find(({ value }) => value === document.docCategory) || {
    label: '',
    value: '',
  },
});

export const documentsViewMachine = setup({
  types: {
    context: {} as {
      documentsService: DocumentsServiceRef;
      form: {
        docCategory: { label: string; value: string };
        docType: { label: string; value: string };
        docName: string;
        uploadedFileName?: string | null;
        docDesc: string;
        id: number;
      };
      modalOpen: boolean;
      fileDrag: boolean;
      // True while a batch of uploads is running; assigned together with `uploads`.
      isUploading: boolean;
      closeModalOnUpload: boolean;
      // MCV-1765: the batch being uploaded, one job at a time. The documents service is
      // single-flight, so the next job is sent only when the previous one has settled.
      uploads: UploadBatch | null;
      providerId?: number;
    },
    events: {} as Events,
    input: {} as {
      providerId?: number;
      documentsService: DocumentsServiceRef;
    },
  },
  actions: {
    // Starts a batch from a drop (values: null, the service falls back to 'other') or from the
    // Add Document modal (every file shares the form values).
    startUploads: enqueueActions(({ context, enqueue, event, self, system }) => {
      let jobs: UploadJob[];
      let rejected: UploadBatch['rejected'] = [];
      let values: UploadBatch['values'] = null;
      let source: UploadBatch['source'];
      if (event.type === 'ON_DROP_UPLOAD') {
        const split = splitUploadable(event.payload.files, STORABLE_EXTENSIONS);
        jobs = buildUploadJobs(
          split.accepted.map((file) => ({ file })),
          false
        );
        rejected = split.rejected;
        source = 'drop';
      } else if (event.type === 'ON_SUBMIT_ADD_FORM') {
        jobs = buildUploadJobs(event.payload.files, event.payload.combine === true);
        values = event.payload.values;
        source = 'modal';
      } else {
        return;
      }
      if (jobs.length === 0) return;

      const { svc, providerId } = resolveUploadTarget({ context, system });
      // The service takes UPLOAD_DOCUMENT only when idle and drops it otherwise, which would
      // leave the batch waiting for an outcome that never comes. This check is valid here:
      // the batch start answers a user event, not one of the service's own.
      if (!svc || !providerId || !svc.getSnapshot().matches('$_IDLE')) {
        console.error('[VIEW] Upload skipped - documentsService is busy or has no providerId', {
          contextProviderId: context.providerId,
          hasService: !!svc,
        });
        sendAlert(system, NOT_READY_ALERT);
        enqueue.assign({ fileDrag: false });
        return;
      }

      const [inFlight, ...pending] = jobs;
      const batch: UploadBatch = {
        providerId,
        inFlight,
        pending,
        total: jobs.length,
        values,
        source,
        outcomes: [],
        rejected,
      };
      enqueue.assign({
        uploads: batch,
        isUploading: true,
        closeModalOnUpload: source === 'modal',
        fileDrag: false,
      });
      sendUpload(svc, self, batch, inFlight);
    }),
    // Runs on every real ON_DOCUMENT_UPLOADED / ON_DOCUMENT_UPLOAD_ERROR from the service:
    // records the outcome, then sends the next job or, after the last one, shows one summary.
    continueUploads: enqueueActions(({ context, enqueue, event, self, system }) => {
      const { uploads } = context;
      if (
        !uploads ||
        (event.type !== 'ON_DOCUMENT_UPLOADED' && event.type !== 'ON_DOCUMENT_UPLOAD_ERROR')
      ) {
        return;
      }
      if (event.type === 'ON_DOCUMENT_UPLOAD_ERROR') {
        console.error('[VIEW] Document upload error:', event.error);
      }
      const outcome = toOutcome(uploads.inFlight, uploads.values, event);
      const outcomes = [...uploads.outcomes, outcome];
      const { svc } = resolveUploadTarget({ context, system });
      // [MCV-1800] A file stored without parsing gets the API's own "Document Upload Complete"
      // notification once it is encrypted, naming the document. This batch summarises the file,
      // so tell the service to let that document's notification pass unannounced (see
      // routeMessage).
      if (outcome.ok && !outcome.parses) {
        svc?.send({ type: 'ON_UPLOAD_NOTICE_OWNED', documentId: outcome.document.id });
      }

      const [next, ...pending] = uploads.pending;
      if (next) {
        const batch = { ...uploads, inFlight: next, pending, outcomes };
        enqueue.assign({ uploads: batch });
        // No idle check: this runs inside the service's own onDone/onError, so its snapshot
        // still reads $_UPLOADING. The send is queued in its mailbox and handled once it is
        // back in $_IDLE.
        if (svc) sendUpload(svc, self, batch, next);
        return;
      }

      summarizeUploads({ ...uploads, outcomes }).forEach((summary) => sendAlert(system, summary));
      enqueue.assign({ uploads: null, isUploading: false });
      const last = outcomes[outcomes.length - 1];
      enqueue.raise({
        type: 'ON_UPLOADS_FINISHED',
        anySucceeded: outcomes.some(({ ok }) => ok),
        // A single dropped file reopens the modal so it can be categorised right away.
        reopen:
          uploads.source === 'drop' && uploads.total === 1 && last.ok
            ? // [MCV-1802] With the dropped file's name, so the edit form can check its format
              // against the File Type chosen next (the API refuses an unparseable CV).
              {
                ...last.document,
                uploadedFileName: last.document.uploadedFileName ?? uploads.inFlight.files[0]?.name,
              }
            : undefined,
      });
    }),
  },
}).createMachine({
  id: 'documentsView',
  initial: '$_MAIN',
  context: ({ input }) => ({
    form: {
      docCategory: { label: '', value: '' },
      docType: { label: '', value: '' },
      docName: '',
      docDesc: '',
      id: -1,
    },
    providerId: input.providerId,
    modalOpen: false,
    fileDrag: false,
    isUploading: false,
    closeModalOnUpload: false,
    uploads: null,
    documentsService: input.documentsService,
  }),
  on: {
    // The service reports each upload of a batch here, whichever state the view is in (the
    // modal may have been closed while the batch runs).
    ON_DOCUMENT_UPLOADED: [
      { guard: ({ context }) => context.uploads !== null, actions: 'continueUploads' },
      {
        // The batch is gone (nothing to attribute the result to).
        actions: ({ event }) => console.warn('[VIEW] Ignoring late ON_DOCUMENT_UPLOADED', event),
      },
    ],
    ON_DOCUMENT_UPLOAD_ERROR: [
      { guard: ({ context }) => context.uploads !== null, actions: 'continueUploads' },
      {
        actions: ({ event }) =>
          console.warn('[VIEW] Ignoring late ON_DOCUMENT_UPLOAD_ERROR', event.error),
      },
    ],
  },
  states: {
    $_MAIN: {
      entry: ({ context, self, system }) => {
        // Get documentsService from system instead of context (more reliable)
        const svc = system?.get(DOCUMENTS_SERVICE) || context.documentsService;

        // Trigger initial load when entering main view
        if (context.providerId && svc) {
          svc.send({
            type: 'GET_DOCUMENTS',
            providerId: context.providerId,
            viewRef: self,
          });
        } else {
          console.warn(
            '[documentsView] Cannot send GET_DOCUMENTS - providerId:',
            context.providerId,
            'documentsService:',
            !!svc
          );
        }
      },
      on: {
        // MCV-605/1190: the view machine is invoked as soon as the vault enters
        // $_DOCUMENTS_VIEW, which can race the provider load. Accept late-arriving
        // provider ids so subsequent uploads have a valid target.
        ON_SET_PROVIDER_ID: {
          actions: assign({
            providerId: ({ event }) =>
              (event as { type: 'ON_SET_PROVIDER_ID'; payload: { providerId: number } }).payload
                .providerId,
          }),
        },
        ON_OPEN_ADD_MODAL: {
          // Open the metadata modal with an empty form BEFORE uploading so the
          // user categorizes the document up front. They pick files from inside
          // the modal; Save uploads them with the shared docCategory/docDesc and each file's own docType.
          target: '$_MODAL',
          actions: [
            assign({
              form: () => ({
                id: -1,
                docName: '',
                docDesc: '',
                docType: { label: '', value: '' },
                docCategory: { label: '', value: '' },
              }),
              modalOpen: true,
              closeModalOnUpload: false,
            }),
          ],
        },
        // A single dropped file that uploaded: open the modal on it so it can be categorised
        // now. The document comes from the event, not from the service's snapshot, which is
        // stale at this point.
        ON_UPLOADS_FINISHED: {
          guard: ({ event }) => event.reopen !== undefined,
          target: '$_MODAL',
          actions: assign({
            form: ({ event }) => formForDocument(event.reopen as UploadedDocument),
            modalOpen: true,
          }),
        },
        ON_DOWNLOAD_FILE: [
          // The service handles this only when idle and stays busy until the last file of a
          // batch, so a click during one would be dropped silently
          {
            guard: ({ context }) => context.uploads !== null,
            actions: ({ system }) =>
              sendAlert(
                system,
                inProgressAlert(
                  'Wait for the current upload to finish, then download the document.'
                )
              ),
          },
          {
            actions: ({ context, event }) => {
              const e = event as { type: string; payload: { id: number } };
              context.documentsService?.send({ type: 'DOWNLOAD_DOCUMENT', id: e.payload.id });
            },
          },
        ],
        ON_DRAG_ENTER: {
          actions: assign({ fileDrag: true }),
        },
        ON_DRAG_LEAVE: {
          actions: assign({ fileDrag: false }),
        },
        ON_DROP_UPLOAD: [
          // The service is single-flight: a drop during a batch would be dropped silently
          {
            guard: ({ context }) => context.uploads !== null,
            actions: [
              assign({ fileDrag: false }),
              ({ system }) =>
                sendAlert(
                  system,
                  inProgressAlert('Wait for the current upload to finish, then drop more files.')
                ),
            ],
          },
          // Empty drop — show a "No file dropped" alert (distinct from the unsupported-type alert)
          {
            actions: [
              assign({ fileDrag: false }),
              ({ system }) =>
                sendAlert(system, {
                  text: 'No file dropped',
                  subText: `Drag and drop a document (${toTypesLabel(STORABLE_EXTENSIONS)}) to upload.`,
                  type: 'error',
                  timeout: 4000,
                }),
            ],
            guard: ({ event }) => event.payload.files.length === 0,
          },
          // Nothing droppable (unsupported type, declared type mismatch or over the size limit):
          // alert and abort before sending to server
          {
            actions: [
              assign({ fileDrag: false }),
              ({ event, system }) =>
                sendAlert(system, {
                  ...getRejectionAlert(
                    splitUploadable(event.payload.files, STORABLE_EXTENSIONS).rejected,
                    STORABLE_EXTENSIONS
                  ),
                  type: 'error',
                  timeout: 6000,
                }),
            ],
            guard: ({ event }) =>
              splitUploadable(event.payload.files, STORABLE_EXTENSIONS).accepted.length === 0,
          },
          // Every file that can be stored uploads, one at a time; the server verifies the bytes
          // and parsing is gated per workflow. Rejected files are named in the summary.
          { actions: 'startUploads' },
        ],
        ON_OPEN_MODAL: {
          target: '$_MODAL',
          actions: [
            assign({
              form: ({ event, context }) => {
                const e = event as { type: string; payload: { id: number } };
                const serviceSnapshot = context.documentsService.getSnapshot();
                const info = serviceSnapshot.context.documentReferences.find(
                  ({ id }) => id === e.payload.id
                );
                if (!info) return { ...context.form };
                return {
                  id: info.id,
                  docName: info.docName,
                  uploadedFileName: info.uploadedFileName,
                  docDesc: info.docDesc,
                  docType: DOCUMENT_TYPES.find(({ value }) => value === info.docType) || {
                    label: '',
                    value: '',
                  },
                  docCategory: DOCUMENT_CATEGORIES.find(
                    ({ value }) => value === info.docCategory
                  ) || {
                    label: '',
                    value: '',
                  },
                };
              },
              modalOpen: true,
              closeModalOnUpload: false,
            }),
          ],
        },
        ON_DELETE_FILE: [
          // Refused during a batch like ON_DOWNLOAD_FILE: the service is busy until its last file
          {
            guard: ({ context }) => context.uploads !== null,
            actions: ({ system }) =>
              sendAlert(
                system,
                inProgressAlert('Wait for the current upload to finish, then delete the document.')
              ),
          },
          {
            actions: ({ context, event }) => {
              const e = event as { type: string; payload: { id: number } };
              context.documentsService?.send({ type: 'DELETE_DOCUMENT', id: e.payload.id });
            },
          },
        ],
        ON_TRIGGER_AI_PARSE: [
          // Refused during a batch like ON_DOWNLOAD_FILE: the service is busy until its last file
          {
            guard: ({ context }) => context.uploads !== null,
            actions: ({ system }) =>
              sendAlert(
                system,
                inProgressAlert('Wait for the current upload to finish, then parse the document.')
              ),
          },
          {
            actions: ({ context, event }) => {
              const e = event as { type: string; payload: { id: number } };
              context.documentsService?.send({ type: 'AI_PARSE_DOCUMENT', id: e.payload.id });
            },
          },
        ],
      },
    },
    $_MODAL: {
      entry: () => console.debug('[VIEW] $_MODAL entry'),
      on: {
        // MCV-605/1190: accept late-arriving provider ids in $_MODAL too, so a
        // user who opens Add Document before the provider finishes loading can
        // still upload once it arrives.
        ON_SET_PROVIDER_ID: {
          actions: assign({
            providerId: ({ event }) =>
              (event as { type: 'ON_SET_PROVIDER_ID'; payload: { providerId: number } }).payload
                .providerId,
          }),
        },
        // The last upload of a batch started from this modal settled: close it when something
        // uploaded. When nothing did, the modal stays open for a retry.
        ON_UPLOADS_FINISHED: {
          guard: ({ context, event }) => context.closeModalOnUpload && event.anySucceeded,
          target: '$_MAIN',
          actions: assign({ modalOpen: false, closeModalOnUpload: false }),
        },
        // Closing the modal does not stop a running batch: it finishes in the background and
        // still shows its summary.
        ON_CLOSE_MODAL: {
          target: '$_MAIN',
          actions: assign({ modalOpen: false, closeModalOnUpload: false }),
        },
        ON_SUBMIT_FORM_ERROR: {
          actions: [({ event }) => console.error('error submitting the document info form', event)],
        },
        ON_SUBMIT_FORM: {
          actions: [
            assign({ modalOpen: false, closeModalOnUpload: false }),
            ({ context, event }) => {
              const e = event as { type: string; payload: { values: DocumentInfoValues } };
              const { id } = context.form;
              context.documentsService?.send({
                type: 'UPDATE_DOCUMENT',
                id,
                values: e.payload.values,
              });
            },
          ],
          target: '$_MAIN',
        },
        // MCV-1785: the modal keeps its inline message and raises the same alert popup a
        // rejected drop gets.
        ON_FILES_REJECTED: {
          actions: ({ event, system }) =>
            sendAlert(system, { ...event.payload, type: 'error', timeout: 6000 }),
        },
        ON_SUBMIT_ADD_FORM: [
          {
            guard: ({ context }) => context.uploads !== null,
            actions: ({ system }) =>
              sendAlert(
                system,
                inProgressAlert('Wait for the current upload to finish, then save again.')
              ),
          },
          { actions: 'startUploads' },
        ],
        ON_CANCEL_FORM: {
          target: '$_MAIN',
          actions: [assign({ modalOpen: false, closeModalOnUpload: false })],
        },
      },
    },
  },
});
